/**
 * Account deletion (danger zone) via TanStack Query.
 *
 * Contract: ``DELETE /me`` permanently and irreversibly deletes the caller's
 * account and all associated data; the server logs the deletion for audit
 * (per the C18 brief). The UI guards this behind a typed-email confirmation
 * before the mutation can fire, and signs the user out on success.
 */
import { useMutation } from "@tanstack/react-query";
import { useApiClient } from "@/api/useApiClient";
import { useAuth } from "@/auth/AuthProvider";

/** Swallow a best-effort sign-out failure: the account is already deleted. */
async function bestEffortSignOut(signOut: () => Promise<void>): Promise<void> {
  try {
    await signOut();
  } catch {
    // Intentionally ignored — the server-side account is already gone.
  }
}

export function useDeleteAccount() {
  const api = useApiClient();
  const { signOut } = useAuth();
  return useMutation({
    mutationFn: () => api.delete<undefined>("/me"),
    onSuccess: () => {
      void bestEffortSignOut(signOut);
    },
  });
}
